import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { X, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '@/components/ui/Toast';
import StarRating from './StarRating';
import { getRatingLabel } from './RatingLabels';
import { reviewService } from '@/services/review.service';
import { ReviewType } from '@/types/review.types';
import type { Review, ReviewFormData, CreateReviewRequest, UpdateReviewRequest } from '@/types/review.types';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetId: string;
  targetTitle: string;
  targetType: ReviewType;
  existingReview?: Review;
  onSuccess?: () => void;
}

type Step = 'rating' | 'success';

export default function ReviewModal({
  isOpen,
  onClose,
  targetId,
  targetTitle,
  targetType,
  existingReview,
  onSuccess,
}: ReviewModalProps) {
  const [step, setStep] = useState<Step>('rating');
  const isEditing = !!existingReview;

  // Mutations
  const createReview = useMutation({
    mutationFn: (data: CreateReviewRequest) => reviewService.createReview(data),
    onSuccess: () => {
      toast.success('Review submitted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit review');
    },
  });

  const updateReview = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReviewRequest }) =>
      reviewService.updateReview(id, data),
    onSuccess: () => {
      toast.success('Review updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update review');
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReviewFormData>({
    defaultValues: {
      rating: existingReview?.rating || 0,
      review_text: existingReview?.review_text || '',
    },
  });

  const rating = watch('rating');
  const reviewText = watch('review_text');

  // Reset form when modal opens/closes or when existingReview changes
  useEffect(() => {
    if (isOpen) {
      setStep('rating');
      reset({
        rating: existingReview?.rating || 0,
        review_text: existingReview?.review_text || '',
      });
    }
  }, [isOpen, existingReview, reset]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const onSubmit = async (data: ReviewFormData) => {
    try {
      if (isEditing && existingReview) {
        await updateReview.mutateAsync({
          id: existingReview._id,
          data: {
            rating: data.rating,
            review_text: data.review_text,
          },
        });
      } else {
        await createReview.mutateAsync({
          review_type: targetType,
          course_id: targetType === ReviewType.COURSE ? targetId : undefined,
          certification_id: targetType === ReviewType.CERTIFICATE ? targetId : undefined,
          learning_path_id: targetType === ReviewType.LEARNING_PATH ? targetId : undefined,
          rating: data.rating,
          review_text: data.review_text,
        });
      }
      setStep('success');
      onSuccess?.();
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const handleClose = () => {
    setStep('rating');
    onClose();
  };

  const handleBack = () => {
    if (step === 'success') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[100] common-modal-overlay"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 common-modal-overlay">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                {step === 'success' ? (
                  <button
                    onClick={handleBack}
                    className="p-1.5 -ml-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft size={20} className="text-gray-600" />
                  </button>
                ) : (
                  <div className="w-8" />
                )}
                <h3
                  className="text-sm font-medium text-gray-500 text-center flex-1 px-2"
                >
                  {targetTitle}
                </h3>
                <button
                  onClick={handleClose}
                  className="p-1.5 -mr-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <AnimatePresence mode="wait">
                {step === 'rating' && (
                  <motion.div
                    key="rating"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="px-5 py-6">
                        {/* Title */}
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">
                          {isEditing ? 'Update your review' : 'Why did you leave this rating?'}
                        </h2>

                        {/* Star Rating */}
                        <div className="flex flex-col items-center mb-6">
                          <Controller
                            name="rating"
                            control={control}
                            rules={{ required: true, min: 1 }}
                            render={({ field }) => (
                              <div className="flex flex-col items-center">
                                <StarRating
                                  value={field.value}
                                  onChange={field.onChange}
                                  size="lg"
                                  className="mb-2"
                                />
                                <span className={`text-sm font-medium transition-opacity duration-200 ${
                                  rating > 0 ? 'text-gray-700 opacity-100' : 'text-gray-400 opacity-0'
                                }`}>
                                  {rating > 0 ? getRatingLabel(rating) : 'Select rating'}
                                </span>
                              </div>
                            )}
                          />
                          {errors.rating && (
                            <p className="text-red-500 text-xs mt-2">Please select a rating</p>
                          )}
                        </div>

                        {/* Review Text */}
                        <div className="mb-2">
                          <Controller
                            name="review_text"
                            control={control}
                            rules={{
                              maxLength: {
                                value: 500,
                                message: 'Review cannot exceed 500 characters',
                              },
                            }}
                            render={({ field }) => (
                              <textarea
                                {...field}
                                placeholder="Tell us about your personal experience..."
                                rows={4}
                                className={`w-full px-4 py-3 text-sm border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none ${
                                  errors.review_text ? 'border-red-500' : 'border-gray-200'
                                }`}
                                maxLength={500}
                              />
                            )}
                          />
                          <div className="flex justify-between items-center mt-1">
                            {errors.review_text ? (
                              <p className="text-red-500 text-xs">{errors.review_text.message}</p>
                            ) : (
                              <span className="text-xs text-gray-400">Optional</span>
                            )}
                            <span className="text-xs text-gray-400">
                              {reviewText?.length || 0}/500
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="px-5 py-4 bg-gray-50 border-t border-gray-100">
                        <button
                          type="submit"
                          disabled={isSubmitting || rating === 0}
                          className="w-full py-3 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                  fill="none"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Submitting...
                            </span>
                          ) : isEditing ? (
                            'Update Review'
                          ) : (
                            'Save and Continue'
                          )}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}

                {step === 'success' && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="px-5 py-12"
                  >
                    <div className="flex flex-col items-center text-center">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          type: 'spring',
                          stiffness: 260,
                          damping: 20,
                          delay: 0.1,
                        }}
                        className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4"
                      >
                        <CheckCircle2 size={32} className="text-green-600" />
                      </motion.div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Thank you for your feedback!
                      </h2>
                      <p className="text-sm text-gray-500 mb-8">
                        Your review helps other learners make informed decisions.
                      </p>
                      <button
                        onClick={handleClose}
                        className="w-full max-w-xs py-3 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 transition-colors duration-200"
                      >
                        Done
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
